// src/pages/SubsystemsPage.tsx
//
// ★ PHASE 6, TASK 18 — Annex A story 27: "Sub-system Information Management".
//
// Administrative metadata about a sub-system or integration point — see
// `029_subsystem_info.sql`'s own header for what "sub-system" means here (no
// clause defines it; PRS2's own reading, registered as A-35). PSD only, both
// read and write. Same shape as `WardClassesPage.tsx` — see that page's own
// header for what is deliberately not built here (a history/point-in-time
// view; the API supports it, this screen shows only the current catalogue).
import { plainProse } from '../lib/plain-words'
import { useState, type FormEvent } from 'react'
import { SgdsButton, SgdsInput, SgdsSelect, SgdsSelectOption, SgdsTextarea } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { VersionHistory } from '../components/VersionHistory'
import { FormResult } from '../components/FormResult'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { BoundedField } from '../components/BoundedField'
import { DatePicker } from '../components/DatePicker'
import { boundsProblems } from '../lib/published-bounds'
import { targetValue } from '../lib/sgds-dom'
import { formatIsoDate, formatInstant } from '../lib/format'
import { SUBSYSTEM_STATUSES, type SubsystemStatusValue } from '../lib/types'
import { EmailReportButton } from '../components/EmailReportButton'
import { ExportToolbar } from '../components/ExportToolbar'
import { StateBadge, stateLabel } from '../components/StatusBadge'

const ALL = 'ALL'
type SubsystemSortKey = 'subsystemCode' | 'name' | 'status' | 'effectiveFrom' | 'integrationEndpoint'

const COLUMN_LABELS: Readonly<Record<SubsystemSortKey, string>> = {
  subsystemCode: 'Code',
  name: 'Name',
  status: 'Status',
  integrationEndpoint: 'Integration endpoint',
  effectiveFrom: 'Effective from',
}

/**
 * ★★ WAVE 8 (Task 3) — ONE copy of each bounded field's label: read by
 * `boundsProblems` for its messages AND by the field that renders it. Two copies
 * drift the day one of them is reworded.
 *
 * ⚠ Only fields the SERVER publishes a bound for appear here. A field the route
 * checks for presence alone is deliberately absent, and its screen therefore says
 * nothing beyond presence — see `routes/administration-field-bounds.ts`.
 */
const BOUNDED_FIELDS = {
  subsystemCode: 'Code',
  name: 'Name',
  note: 'Note (optional)',
} as const

/**
 * ★★ WAVE 8 (Task 3) REVIEW FINDING — the bounded field that ONLY EXISTS ON
 * AN ESTABLISH, and is therefore not part of a RETIRE's problem set.
 *
 * ⚠ Ticking the "Retire … instead of superseding it" box unmounts it and drops
 * it from the request body, but the React state lives on this page and survives
 * the unmount. Without naming it here, a value typed before the box was ticked
 * keeps `problems.any` true and **disables Save with no error visible anywhere** —
 * the Wave 5 failure `components/FieldError.tsx`'s header records verbatim. See
 * `lib/published-bounds.ts`'s `BoundsProblemOptions` for the full reasoning.
 *
 * ⚠ This list is the SUBMITTED half of `handleSubmit`'s own `!retireInstead &&`
 * spread below. If one changes, the other has to.
 */
const ESTABLISH_ONLY_FIELDS = ['name'] as const

type FormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string }
  | { status: 'success'; message: string }

export function SubsystemsPage() {
  // ★★★ WAVE 12 (P4) — the version history and its bi-temporal rewind. The series
  // route has answered since Phase 6 and NOTHING called it until this wave; the
  // RTM's Annex A 26-28 row carried that as a gap, because an endpoint no client
  // calls is indistinguishable from one that does not work.
  //
  // ⚠ The fetcher RESOLVES TO NULL until a code is chosen rather than calling the
  // route with an empty key: that URL is the LIST route, which would return the
  // whole catalogue into a component that renders it as one code's history.
  const [historySubject, setHistorySubject] = useState('')
  const [historyAsRecordedAt, setHistoryAsRecordedAt] = useState('')
  const history = useApi(
    () =>
      historySubject.length === 0
        ? Promise.resolve(null)
        : api.subsystemSeries(historySubject, historyAsRecordedAt),
    [historySubject, historyAsRecordedAt],
  )
  const { sort, onSort, interacted } = useSort<SubsystemSortKey>()
  const [statusFilter, setStatusFilter] = useState<string>(ALL)
  const subsystems = useApi(
    () => api.subsystems({ ...sortParams(sort), ...(statusFilter !== ALL && { status: statusFilter as SubsystemStatusValue }) }),
    [sort.column, sort.dir, statusFilter],
  )

  const [subsystemCode, setSubsystemCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [integrationEndpoint, setIntegrationEndpoint] = useState('')
  const [status, setStatus] = useState<SubsystemStatusValue>('ACTIVE')
  const [operationalNote, setOperationalNote] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [note, setNote] = useState('')
  const [retireInstead, setRetireInstead] = useState(false)
  const [form, setForm] = useState<FormState>({ status: 'idle' })

  const rows = subsystems.data?.subsystems ?? []
  const total = subsystems.data?.total ?? 0

  /*
    ⚠ The bounds come from the PAYLOAD (`validation`), never from this file, and
    they are checked against the value that will actually be SENT rather than the
    keystrokes in the box — see `lib/published-bounds.ts` for why both of those
    matter. `undefined` while the list is loading or refused, in which case the
    fields show nothing beyond `required` and the route refuses on its own.
  */
  const validation = subsystems.data?.validation
  const problems = boundsProblems(
    validation,
    {
      subsystemCode: subsystemCode.trim().toUpperCase(),
      name: name.trim(),
      note: note.trim(),
    },
    BOUNDED_FIELDS,
    // ⚠ A RETIRE sends none of the establish-only fields, so none of them can be
    // in breach — see ESTABLISH_ONLY_FIELDS above for the disabled-with-no-message
    // defect this closes.
    { notSubmitted: retireInstead ? ESTABLISH_ONLY_FIELDS : [] },
  )

  const canSubmit =
    subsystemCode.trim().length > 0 &&
    effectiveFrom.length > 0 &&
    !problems.any &&
    (retireInstead || name.trim().length > 0)

  /**
   * ★★ WAVE 26 — Save FREEZES a sentence and asks; the write happens only from the
   * confirmation (`ConfirmWrite`'s header says why every irreversible write does
   * this now). The sentence is composed here, once, from the fields as they were
   * when Save was pressed — never re-derived from a form the officer may since
   * have edited. The consequence stated is the honest one: nothing in PRS2 reads
   * this catalogue to decide anything, so what changes is the record and its exports.
   */
  const [confirming, setConfirming] = useState<string | null>(null)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    const upper = subsystemCode.trim().toUpperCase()
    setConfirming(
      retireInstead
        ? `Retire sub-system ${upper} from ${effectiveFrom}. No successor window opens, so the catalogue and its PDF/Excel exports list no record for it on or after that day. There is no undo. A mistake is corrected by another version.`
        : `Supersede sub-system ${upper} from ${effectiveFrom}: "${name.trim()}", status ${stateLabel(status).toLowerCase()}. The record in force closes the day before, and the catalogue and its PDF/Excel exports show the new record from that day. There is no undo. A mistake is corrected by another version.`,
    )
  }

  const performWrite = async () => {
    setConfirming(null)
    setForm({ status: 'submitting' })
    try {
      const outcome = (await api.supersedeSubsystem(subsystemCode.trim().toUpperCase(), {
        effectiveFrom,
        establish: !retireInstead,
        ...(note.trim().length > 0 && { note: note.trim() }),
        ...(!retireInstead && {
          name: name.trim(),
          description: description.trim().length > 0 ? description.trim() : null,
          integrationEndpoint: integrationEndpoint.trim().length > 0 ? integrationEndpoint.trim() : null,
          status,
          operationalNote: operationalNote.trim().length > 0 ? operationalNote.trim() : null,
        }),
      })) as { outcome: string }
      setForm({
        status: 'success',
        message:
          outcome.outcome === 'RETIRED'
            ? `${subsystemCode.trim().toUpperCase()} retired from ${effectiveFrom}.`
            : `${subsystemCode.trim().toUpperCase()} superseded from ${effectiveFrom}.`,
      })
      setSubsystemCode('')
      setName('')
      setDescription('')
      setIntegrationEndpoint('')
      setOperationalNote('')
      setEffectiveFrom('')
      setNote('')
      subsystems.reload()
    } catch (error) {
      setForm({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not save this sub-system.' })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap pt-no-print">
        <div>
          <h1 className="text-3xl mb-2">Sub-systems</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            Administrative metadata about a sub-system or integration point. Effective-dated,
            PSD-only. Nothing in PRS2 reads this catalogue to decide anything: it is administered and searchable
            and nothing more.
          </p>
        </div>
        <div className="flex gap-2">
          {/*
            ★★★ WAVE 27 — one control group, owner-directed. These answer a single
            question — "what do I want to do with this list?" — and were arranged as if
            they answered three or four. See `ExportToolbar` for the whole argument.
          */}
          <ExportToolbar
            onDownload={(format) => {
              window.location.href = api.subsystemExportUrl(format)
            }}
            email={<EmailReportButton path="/api/administration/subsystems/export" />}
          />
        </div>
      </div>

      <div className="pt-card pt-controlbar pt-no-print">
        <SgdsSelect label="Status" value={statusFilter} onSgdsChange={(e) => setStatusFilter(targetValue(e))}>
          <SgdsSelectOption value={ALL}>All statuses</SgdsSelectOption>
          {SUBSYSTEM_STATUSES.map((s) => (
            <SgdsSelectOption key={s} value={s}>
              {stateLabel(s)}
            </SgdsSelectOption>
          ))}
        </SgdsSelect>
      </div>

      <SortAnnouncement sort={sort} interacted={interacted} busy={subsystems.loading} columnLabels={COLUMN_LABELS} rowCount={rows.length} />

      {subsystems.error !== null ? (
        <ErrorCard title="Could not load sub-systems" message={subsystems.error} onRetry={subsystems.reload} />
      ) : subsystems.initialLoading ? (
        <LoadingCard label="Loading sub-systems" rows={4} />
      ) : rows.length === 0 ? (
        <EmptyCard
          title="No sub-systems in scope"
          message="Either nothing matches this filter, or your active identity is not PSD: this catalogue is administered by PSD only."
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap" aria-busy={subsystems.loading}>
            <table className="pt-table">
              <caption className="sr-only">Sub-system records currently in force</caption>
              <thead>
                <tr>
                  <SortableTh label="Code" sortKey="subsystemCode" sort={sort} onSort={onSort} />
                  <SortableTh label="Name" sortKey="name" sort={sort} onSort={onSort} />
                  <SortableTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
                  <SortableTh label="Integration endpoint" sortKey="integrationEndpoint" sort={sort} onSort={onSort} />
                  <SortableTh label="Effective from" sortKey="effectiveFrom" sort={sort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="font-semibold pt-mono">{row.subsystemCode}</td>
                    <td>{plainProse(row.name)}</td>
                    <td>
                      <StateBadge value={row.status} />
                    </td>
                    <td className="text-sm pt-mono">{row.integrationEndpoint === null ? 'None' : plainProse(row.integrationEndpoint)}</td>
                    <td className="text-sm" title={formatInstant(row.recordedAt)}>
                      {formatIsoDate(row.effective.from)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-[var(--sgds-color-subtle)] px-5 py-3 border-t border-[var(--sgds-border-color-muted)] pt-no-print">
            Showing {rows.length} of {total} currently in-force sub-system record{total === 1 ? '' : 's'}.{' '}
            {sort.column === null ? 'The server default order.' : `Sorted by ${COLUMN_LABELS[sort.column]} (${sort.dir}).`}
          </p>
        </div>
      )}

      {/* ★★★ WAVE 12 (P4) — the version history, below the catalogue it belongs to.
          The code picker lives in the component rather than as a column on this
          table: twelve tables of differing shapes meant twelve thead/tbody edits,
          twelve chances to break a working screen for one control. */}
      <VersionHistory
        subject={historySubject}
        subjectOptions={rows.map((row) => row.subsystemCode)}
        subjectHint="The sub-system codes listed above. Sort or filter that table to reach others."
        onSubjectChange={(next) => {
          // ⚠ Changing code RESETS the rewind: an instant chosen while reading one
          // code is not a statement about another, and carrying it over would
          // silently reconstruct the new one at a forgotten instant.
          setHistoryAsRecordedAt('')
          setHistorySubject(next)
        }}
        entries={history.data?.entries ?? []}
        loading={history.loading}
        error={history.error}
        onRetry={history.reload}
        asRecordedAt={historyAsRecordedAt}
        onAsRecordedAtChange={setHistoryAsRecordedAt}
        columns={[
            { header: 'Name', cell: (entry) => entry.name },
            { header: 'Status', cell: (entry) => <StateBadge value={entry.status} /> },
        ]}
      />
      <div className="pt-card flex flex-col gap-5 pt-no-print">
        <div>
          <h2 className="text-xl mb-1">Establish or retire a sub-system record</h2>
          <p className="text-sm text-[var(--sgds-body-color-subtle)]">
            Superseding is the only way a record changes: there is no edit-in-place.
          </p>
        </div>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
          <BoundedField
            label={BOUNDED_FIELDS.subsystemCode}
            example="e.g. FILEXCH."
            value={subsystemCode}
            onValueChange={setSubsystemCode}
            bounds={validation?.subsystemCode}
            problem={problems.of.subsystemCode}
            required
          />
          {/* A plain native date input — see WardClassesPage.tsx's own comment for why not SgdsInput/ServiceDatePicker. */}
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold">
              Effective from <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </span>
            <DatePicker value={effectiveFrom} onChange={setEffectiveFrom} required />
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={retireInstead} onChange={(e) => setRetireInstead(e.target.checked)} />
            Retire this sub-system instead of superseding it (no successor window opens)
          </label>
          {!retireInstead && (
            <>
              <BoundedField
                label={BOUNDED_FIELDS.name}
                value={name}
                onValueChange={setName}
                bounds={validation?.name}
                problem={problems.of.name}
                required
              />
              <SgdsSelect label="Status" value={status} onSgdsChange={(e) => setStatus(targetValue(e) as SubsystemStatusValue)}>
                {SUBSYSTEM_STATUSES.map((s) => (
                  <SgdsSelectOption key={s} value={s}>
                    {stateLabel(s)}
                  </SgdsSelectOption>
                ))}
              </SgdsSelect>
              <SgdsInput placeholder=""
                label="Integration endpoint (optional)"
                value={integrationEndpoint}
                onSgdsInput={(e) => setIntegrationEndpoint(targetValue(e))}
              />
              <SgdsInput placeholder="" label="Description (optional)" value={description} onSgdsInput={(e) => setDescription(targetValue(e))} />
              <div className="sm:col-span-2">
                <SgdsTextarea placeholder=""
                  label="Operational note (optional)"
                  value={operationalNote}
                  onSgdsInput={(e) => setOperationalNote(targetValue(e))}
                />
              </div>
            </>
          )}
          <BoundedField
            className="sm:col-span-2"
            label={BOUNDED_FIELDS.note}
            value={note}
            onValueChange={setNote}
            bounds={validation?.note}
            problem={problems.of.note}
            multiline
          />
          {confirming !== null && (
            <ConfirmWrite
              summary={confirming}
              confirmLabel="Yes, apply it"
              onConfirm={() => void performWrite()}
              onCancel={() => setConfirming(null)}
            />
          )}
          <FormResult state={form} className="sm:col-span-2" />
          <div className="sm:col-span-2">
            <SgdsButton type="submit" variant="primary" disabled={!canSubmit || confirming !== null} loading={form.status === 'submitting'}>
              {retireInstead ? 'Retire' : 'Save'}
            </SgdsButton>
          </div>
        </form>
      </div>
    </div>
  )
}
